import { createContext, type Dispatch, type SetStateAction } from 'react';
import {
  type ModalContentKind,
  type ModalContentState,
} from '@common/types/modal';
import cn from 'classnames';
import { type TargetAndTransition } from 'framer-motion';

import css from './styles.module.scss';

const ModalContext = createContext(
  {} as {
    isOpen: boolean;
    openModal: (modalContent: ModalContentState) => void;
    closeModal: () => void;
    modalContent: ModalContentState;
    boostProductionDynamicData: { inPool: number; price: number } | undefined;
    setBooostProductionDynamicData: Dispatch<
      SetStateAction<{ inPool: number; price: number } | undefined>
    >;
    secondModal: {
      isOpen: boolean;
      openModal: (modalContent: ModalContentState) => void;
      closeModal: () => void;
      modalContent: ModalContentState;
    };
    thirdModal: {
      isOpen: boolean;
      openModal: (modalContent: ModalContentState) => void;
      closeModal: () => void;
      modalContent: ModalContentState;
    };
    setOverlayIsActive: Dispatch<SetStateAction<boolean>>;
    headerVisible: boolean;
  },
);

const commonModalStyles = {
  modal: 'bg-black/60 backdrop-blur-[10px] h-full',
  wrapper: 'bg-transparent backdrop-blur-[8px]',
};

const modalsContentConfig: Partial<
  Record<
    ModalContentKind,
    {
      classNames?: {
        wrapper?: string;
        modal?: string;
        content?: string;
      };
      cancelOutsideClickClose?: boolean;
      headerVisible?: boolean;
      animate?: TargetAndTransition;
      exit?: TargetAndTransition;
    }
  >
> = {
  claimResources: {
    classNames: {
      modal:
        '!overflow-visible pt-[1px] shadow-[0_1px_0_0_rgba(255,255,255,0.25)_inset]',
    },
    cancelOutsideClickClose: true,
  },
  userLevelUp: {
    classNames: {
      modal: 'bg-transparent h-full rounded-none shadow-none pb-0',
      wrapper: 'bg-black/80 backdrop-blur-[10px]',
      content: 'h-full',
    },
    headerVisible: true,
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  takeContract: {
    classNames: {
      modal: 'pt-[1px]',
    },
  },
  chestInfo: {
    classNames: { modal: cn(css.boxShadowChest, 'overflow-hidden') },
  },
  boostProduction: {
    classNames: {
      modal: cn('bg-main-bg/70 backdrop-blur-3xl', css.overlay),
      wrapper: '!bg-transparent',
    },
  },
  boostFactory: {
    classNames: {
      modal: cn('bg-main-bg backdrop-blur-3xl', css.overlay),
    },
  },
  coinDeposit: {
    classNames: {
      modal: cn(commonModalStyles.modal, 'z-10'),
      wrapper: commonModalStyles.wrapper,
    },
    headerVisible: true,
  },
  buyMarketBox: {
    classNames: {
      modal: 'bg-transparent backdrop-blur-[10px] h-full',
      wrapper: 'bg-black/60 backdrop-blur-[8px]',
      content: 'h-full',
    },
  },
  tonWalletInfo: {
    classNames: {
      modal: 'overflow-hidden',
    },
    cancelOutsideClickClose: true,
  },
  buyVip: {
    classNames: {
      modal: 'bg-transparent backdrop-blur-[10px] h-full',
      wrapper: 'bg-black/60 backdrop-blur-[8px]',
      content: 'w-full h-full',
    },
  },
  buySingleTgems: {
    classNames: {
      modal: cn(commonModalStyles.modal, 'z-[11]'),
      wrapper: commonModalStyles.wrapper,
      content: 'h-full',
    },
    headerVisible: true,
  },
  buyBoostX5X10: {
    classNames: commonModalStyles,
  },
  earnTonFromPartners: {
    classNames: {
      modal: css.boxShadowChest,
    },
  },
  revenueDetails: {
    classNames: {
      modal: css.boxShadowChest,
    },
  },
  walletEarnDetails: {
    classNames: {
      modal: css.boxShadowChest,
    },
  },
  buyArenaOffer: {
    classNames: {
      ...commonModalStyles,
      content: 'h-full',
    },
    headerVisible: true,
  },
  arenaOfferPopup: {
    classNames: commonModalStyles,
  },
  withdrawModal: {
    classNames: commonModalStyles,
  },
  burgerSettings: {
    classNames: {
      modal: 'bg-[#181A20B2] backdrop-blur-[2px] h-full',
      wrapper: 'bg-transparent',
      content:
        'w-[80%] px-4 pt-[calc(var(--tg-safe-area-inset-top,_0px)_+_100px)]',
    },
  },
  buyUsdtModal: {
    classNames: {
      modal: 'overflow-hidden z-[12]',
    },
    cancelOutsideClickClose: true,
    headerVisible: true,
  },
  thanksForPurchase: {
    classNames: {
      modal: cn(commonModalStyles.modal, '!bg-black/80 !rounded-t-none'),
      wrapper: cn(commonModalStyles.wrapper, '!z-[9]'),
      content: 'h-full',
    },
    headerVisible: true,
  },
  buyPaidRewardLine: {
    classNames: {
      modal: cn(commonModalStyles.modal, '!bg-black/80'),
      wrapper: cn(commonModalStyles.wrapper, '!z-[11]'),
    },
  },
  buyDiceModal: {
    classNames: {
      modal: cn(commonModalStyles.modal, 'rounded-none !bg-black/80'),
      wrapper: commonModalStyles.wrapper,
      content: 'pt-[10rem]',
    },
    headerVisible: true,
  },
  buyBingoBallsModal: {
    classNames: {
      modal: cn(commonModalStyles.modal, 'rounded-none !bg-black/80'),
      wrapper: commonModalStyles.wrapper,
      content: 'pt-[10rem]',
    },
    headerVisible: true,
  },
  buyScratchTicketModal: {
    classNames: {
      modal: cn(commonModalStyles.modal, 'rounded-none !bg-black/80'),
      wrapper: commonModalStyles.wrapper,
      content: 'pt-[10rem]',
    },
    headerVisible: true,
  },
  productOfDayModal: {
    classNames: {
      modal: 'overflow-hidden !pb-0',
    },
  },
  buyProductOfDayUpgrade: {
    classNames: {
      modal: cn(commonModalStyles.modal, 'rounded-none !bg-black/80'),
      wrapper: commonModalStyles.wrapper,
      content: 'h-full',
    },
    headerVisible: true,
  },
  productOfDaySpin: {
    classNames: {
      modal: cn(commonModalStyles.modal, 'rounded-none !bg-black/80 !pb-0'),
      wrapper: commonModalStyles.wrapper,
      content: 'h-full',
    },
    headerVisible: true,
  },
};

export { ModalContext, modalsContentConfig };
